//@ts-strict-ignore
//Same as above, but as a hook and with memoization of filters to help avoid accidental re-renders.
import { useEffect, useMemo } from 'react';
import * as FilterHelper from './FilterHelper';
import { Direction, IFilterInput } from '../graphql/schema/p4types';
import { useCompany } from './FetchConfig';

//All currently loaded views (May cover multiple different workspaces).
export type AllViews = {
  [key: string]: WorkspaceViews;
};
//Views for a particular screenName (E.g. Customer Workspace Views).
type WorkspaceViews = {
  currentView: View;
  view: {
    [key: string]: View;
  };
};

//TODO - View Typing.
type View = any;

type ViewConfig<Entity> = {
  viewScreenName: string; //Unique identifier for this view.
  location: any; //Current location, e.g. props.location or 'useLocation()' result.
  predefinedFilters?: Array<IFilterInput>;
  views: AllViews;
  altView: View;
  initialSortColumn?: keyof Entity | string;
  initialSortDir?: Direction;
};

export const useView = <Entity>(
  {
    viewScreenName,
    location,
    predefinedFilters,
    views,
    altView: altViewFromProps,
    initialSortColumn,
    initialSortDir,
  }: ViewConfig<Entity>,
  screen?: string,
  predefinedFilter?: Array<IFilterInput>,
  altView?: any
) => {
  const screenName = useMemo(() => {
    let newScreenName = viewScreenName || screen || location?.pathname.slice(1);
    if (newScreenName.endsWith('/')) newScreenName = newScreenName.slice(0, -1);
    return newScreenName;
  }, [location?.pathname, screen, viewScreenName]);

  const presetFilters = useMemo(() => {
    return [...(predefinedFilter || []), ...(predefinedFilters || [])];
  }, [predefinedFilter, predefinedFilters]);

  const foundView = useMemo(() => {
    let newFoundView: View = null;
    if (altView) {
      newFoundView = altView;
    } else if (views?.[screenName]) {
      newFoundView = altViewFromProps ? views[screenName].view[altViewFromProps] : views[screenName].currentView;
    }
    return newFoundView;
  }, [altView, altViewFromProps, screenName, views]);

  const company = useCompany();

  const memoisedFilters = useMemo(() => {
    return FilterHelper.getFilters(foundView, presetFilters, company, views?.[screenName]?.availableColumns ?? []);
  }, [company, foundView, presetFilters, screenName, views]);

  const [orderBy, orderDirection] = useMemo(() => {
    const orderByElement = foundView?.orderBy?.[0];

    let newOrderDirection: string;
    if (orderByElement) {
      newOrderDirection = orderByElement.isDescending ? Direction.Desc : Direction.Asc;
    } else {
      newOrderDirection = initialSortDir ?? Direction.Asc;
    }
    return [orderByElement?.fieldName ?? initialSortColumn ?? 'id', newOrderDirection];
  }, [foundView?.orderBy, initialSortColumn, initialSortDir]);

  const totalColumns = useMemo(() => {
    return foundView?.columns.filter((col) => col.total).map((col) => col.fieldName);
  }, [foundView?.columns]);

  return useMemo(
    () => ({
      orderBy: orderBy,
      orderDirection: orderDirection,
      filters: memoisedFilters,
      viewColumns: foundView?.columns,
      totalColumns: totalColumns,
      skip: !foundView && !views?.[screenName],
      currentView: foundView,
    }),
    [foundView, memoisedFilters, orderBy, orderDirection, screenName, totalColumns, views]
  );
};
